import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  LineChart, Line,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../utils/api';
import './Dashboard.css';

// ─── Color palette aligned with ZipQA primaries ───────────────────────────────

// ─── Constants ────────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#6366f1','#10b981','#f59e0b','#ef4444',
  '#3b82f6','#8b5cf6','#14b8a6','#f97316',
];

const CHART_TYPES = [
  { value: 'bar',    label: 'Bar Chart',   icon: '📊', desc: 'Compare values across categories' },
  { value: 'pie',    label: 'Pie Chart',   icon: '🥧', desc: 'Show proportional distribution' },
  { value: 'line',   label: 'Line Chart',  icon: '📈', desc: 'Track trends over time' },
  { value: 'area',   label: 'Area Chart',  icon: '📉', desc: 'Show cumulative trends' },
  { value: 'number', label: 'Number',      icon: '🔢', desc: 'Display a single metric' },
  { value: 'table',  label: 'Table',       icon: '📋', desc: 'Detailed data in rows & columns' },
];

const DATA_SOURCES = [
  { value: 'test_runs',     label: 'Test Runs',     icon: '▶️' },
  { value: 'test_results',  label: 'Test Results',  icon: '🧪' },
  { value: 'test_cases',    label: 'Test Cases',    icon: '✅' },
  { value: 'requirements',  label: 'Requirements',  icon: '📋' },
];

const GROUP_BY_OPTIONS = {
  test_runs:    [
    { value: 'status',            label: 'Status' },
    { value: 'runType',           label: 'Run Type' },
    { value: 'platform',          label: 'Platform' },
    { value: 'environment',       label: 'Environment' },
    { value: 'createdAt_day',     label: 'Day' },
    { value: 'createdAt_week',    label: 'Week' },
    { value: 'createdAt_month',   label: 'Month' },
  ],
  test_results: [
    { value: 'status',  label: 'Result Status' },
  ],
  test_cases:   [
    { value: 'status',    label: 'Status' },
    { value: 'priority',  label: 'Priority' },
    { value: 'severity',  label: 'Severity' },
    { value: 'category',  label: 'Category' },
  ],
  requirements: [
    { value: 'status',   label: 'Status' },
    { value: 'priority', label: 'Priority' },
  ],
};

const METRIC_OPTIONS = [
  { value: 'count',      label: 'Count' },
  { value: 'passRate',   label: 'Pass Rate (%)' },
  { value: 'failRate',   label: 'Fail Rate (%)' },
  { value: 'avgResults', label: 'Avg Results' },
];

const DATE_RANGES = [
  { value: '',        label: 'All Time' },
  { value: 'today',   label: 'Today' },
  { value: 'week',    label: 'This Week' },
  { value: 'month',   label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year',    label: 'This Year' },
];

const SIZES = [
  { label: '1/4',  w: 3 },
  { label: '1/3',  w: 4 },
  { label: '1/2',  w: 6 },
  { label: '2/3',  w: 8 },
  { label: 'Full', w: 12 },
];

const EMPTY_WIDGET = {
  title: '',
  type: 'bar',
  dataSource: 'test_runs',
  filters: { runType: '', status: '', dateRange: '', assignedTo: '' },
  groupBy: 'status',
  metric: 'count',
  customQuery: '',
  layout: { x: 0, y: 0, w: 6, h: 4 },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeChartData(raw) {
  // Backend returned an error object — surface it
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.error) {
    return { error: raw.error };
  }

  // Backend already returns { labels, datasets } — pass through directly
  if (raw && !Array.isArray(raw) && Array.isArray(raw.labels) && Array.isArray(raw.datasets)) {
    // Map dataset colors to our palette if missing
    return {
      labels: raw.labels,
      datasets: raw.datasets.map((ds, i) => ({
        ...ds,
        color: ds.color || CHART_COLORS[i % CHART_COLORS.length],
      })),
    };
  }

  // Raw array format: [{_id, count|value}, ...]
  if (!raw || !Array.isArray(raw)) return { labels: [], datasets: [] };
  if (raw.length === 0) return { labels: [], datasets: [] };

  // Number widget — single value
  if (raw.length === 1 && (raw[0]._id === null || raw[0]._id === undefined) && raw[0].value !== undefined) {
    return { single: raw[0].value };
  }

  const labels = raw.map(r => String(r._id ?? r.label ?? 'Unknown'));
  const data   = raw.map(r => Number(r.value ?? r.count ?? 0));
  return { labels, datasets: [{ label: 'Count', data, color: CHART_COLORS[0] }] };
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="db-tooltip">
      <p className="db-tooltip-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || CHART_COLORS[i] }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</strong>
        </p>
      ))}
    </div>
  );
}

// ─── Chart Renderer ───────────────────────────────────────────────────────────

function ChartRenderer({ type, data }) {
  if (!data) return <div className="db-empty-chart">No data</div>;

  // Error from backend
  if (data.error) return <div className="db-empty-chart">{data.error}</div>;

  if (data.single !== undefined) {
    return (
      <div className="db-number-widget">
        <span className="db-number-value">{Number(data.single).toLocaleString()}</span>
      </div>
    );
  }

  const { labels = [], datasets = [] } = data;
  if (labels.length === 0) return <div className="db-empty-chart">No data available</div>;

  // Reshape for recharts
  const chartData = labels.map((lbl, i) => {
    const point = { name: lbl };
    datasets.forEach(ds => { point[ds.label] = ds.data[i] ?? 0; });
    return point;
  });

  const commonProps = {
    data: chartData,
    margin: { top: 8, right: 8, bottom: 4, left: -12 },
  };

  const axisProps = {
    cartesian: (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </>
    ),
  };

  if (type === 'table') {
    return (
      <div className="db-table-wrap">
        <table className="db-table">
          <thead><tr><th>Label</th>{datasets.map(ds => <th key={ds.label}>{ds.label}</th>)}</tr></thead>
          <tbody>
            {labels.map((lbl, i) => (
              <tr key={i}>
                <td>{lbl}</td>
                {datasets.map(ds => <td key={ds.label}>{ds.data[i] ?? 0}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'pie') {
    const pieData = labels.map((lbl, i) => ({ name: lbl, value: datasets[0]?.data[i] ?? 0 }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={pieData} cx="50%" cy="50%" outerRadius="70%" dataKey="value"
            labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
            {pieData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart {...commonProps}>
          {axisProps.cartesian}
          {datasets.map((ds, i) => (
            <Line key={ds.label} type="monotone" dataKey={ds.label}
              stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'area') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart {...commonProps}>
          {axisProps.cartesian}
          {datasets.map((ds, i) => (
            <Area key={ds.label} type="monotone" dataKey={ds.label}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              fill={CHART_COLORS[i % CHART_COLORS.length] + '33'} strokeWidth={2} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Default: bar
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart {...commonProps}>
        {axisProps.cartesian}
        {datasets.map((ds, i) => (
          <Bar key={ds.label} dataKey={ds.label} fill={CHART_COLORS[i % CHART_COLORS.length]}
            radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Widget Card ──────────────────────────────────────────────────────────────

function WidgetCard({ widget, dashboardId, onEdit, onDelete, onResize }) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.post(`/dashboards/${dashboardId}/widgets/${widget._id}/data`, {});
      // res.data = { success, data: {labels,datasets} | [{_id,count}] }
      const payload = res.data?.data ?? res.data ?? [];
      console.log('[WidgetCard] raw payload:', JSON.stringify(payload));
      const normalized = normalizeChartData(payload);
      console.log('[WidgetCard] normalized:', JSON.stringify(normalized));
      if (normalized?.error) {
        setError(normalized.error);
      } else {
        setChartData(normalized);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load widget data');
    } finally {
      setLoading(false);
    }
  }, [dashboardId, widget._id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartType = CHART_TYPES.find(t => t.value === widget.type);
  const ds = DATA_SOURCES.find(d => d.value === widget.dataSource);

  return (
    <div className="db-widget-card" style={{ '--w': widget.layout?.w || 6 }}>
      {/* Header */}
      <div className="db-widget-header">
        <span className="db-widget-title">
          <span style={{ fontSize: 15 }}>{chartType?.icon}</span>
          {widget.title || 'Untitled Widget'}
        </span>
        <div className="db-widget-actions">
          <span style={{ fontSize: 11, color: 'var(--zq-text-muted)', marginRight: 6 }}>
            {ds?.icon} {ds?.label}
          </span>
          <button className="db-icon-btn" title="Refresh" onClick={fetchData}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M13.5 2.5A6.5 6.5 0 1 1 2.5 8" strokeLinecap="round"/>
              <polyline points="13.5,2.5 13.5,6 10,6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="db-icon-btn" title="Edit widget" onClick={() => onEdit(widget)}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M11.5 2.5l2 2-9 9H2.5v-2l9-9z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="db-icon-btn danger" title="Remove widget" onClick={() => onDelete(widget._id)}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="2" y1="2" x2="14" y2="14" strokeLinecap="round"/>
              <line x1="14" y1="2" x2="2" y2="14" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Resize strip */}
      <div className="db-size-row">
        <span style={{ fontSize: 10, color: 'var(--zq-text-muted)', marginRight: 6, textTransform: 'uppercase', letterSpacing: '.4px', fontWeight: 600 }}>Width</span>
        {SIZES.map(s => (
          <button
            key={s.w}
            className={`db-size-pill ${widget.layout?.w === s.w ? 'active' : ''}`}
            onClick={() => onResize(widget._id, s.w)}
          >{s.label}</button>
        ))}
      </div>

      {/* Chart area */}
      <div className="db-widget-body">
        {loading && (
          <div className="db-widget-loading">
            <div className="db-spinner" />
            <span>Loading…</span>
          </div>
        )}
        {!loading && error && (
          <div className="db-widget-error">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ marginRight: 6 }}>
              <circle cx="8" cy="8" r="7"/><line x1="8" y1="5" x2="8" y2="8.5" strokeLinecap="round"/><circle cx="8" cy="11" r=".6" fill="currentColor"/>
            </svg>
            {error}
          </div>
        )}
        {!loading && !error && (
          <div style={{ width: '100%', height: widget.type === 'table' ? 'auto' : 220 }}>
            <ChartRenderer type={widget.type} data={chartData} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Widget Modal ─────────────────────────────────────────────────────────────

const CATEGORY_ORDER = ['Status', 'Activity', 'Progress', 'Quality', 'Workload', 'Test Cases', 'Requirements'];

function WidgetModal({ initial, projectId, dashboardId, onSave, onClose, onApplied, initialTab }) {
  const isEdit = !!initial?._id;
  // When editing an existing widget, always land on the Custom tab
  const [tab, setTab] = useState(isEdit ? 'custom' : (initialTab || 'library'));

  // ── Custom tab state ──────────────────────────────────────────────────────
  const [form, setForm] = useState({
    ...EMPTY_WIDGET,
    ...initial,
    filters: { ...EMPTY_WIDGET.filters, ...(initial?.filters || {}) },
    layout:  { ...EMPTY_WIDGET.layout,  ...(initial?.layout  || {}) },
  });
  const [advanced, setAdvanced]       = useState(false);
  const [preview, setPreview]         = useState(null);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevError, setPrevError]     = useState('');

  // ── Library tab state ─────────────────────────────────────────────────────
  const [templates, setTemplates]     = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [libTab, setLibTab]           = useState('All');
  const [libSaving, setLibSaving]     = useState(false);
  const [libLoadErr, setLibLoadErr]   = useState('');

  useEffect(() => {
    if (tab === 'library' && templates.length === 0 && !libLoadErr) {
      api.get('/dashboards/widget-templates')
        .then(r => setTemplates(r.data?.data || []))
        .catch(() => setLibLoadErr('Failed to load widget templates'));
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const libCategories = ['All', ...CATEGORY_ORDER.filter(c => templates.some(t => t.category === c))];
  const libVisible    = libTab === 'All' ? templates : templates.filter(t => t.category === libTab);

  function toggleLib(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function selectAllLib() { setSelected(new Set(libVisible.map(t => t.id))); }
  function clearLib()     { setSelected(new Set()); }

  async function applyLibrary() {
    if (selected.size === 0) return;
    const toAdd = templates.filter(t => selected.has(t.id));
    setLibSaving(true);
    try {
      await api.post(`/dashboards/${dashboardId}/apply-template`, { widgets: toAdd, projectId });
      onApplied && onApplied();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to apply widgets');
    } finally {
      setLibSaving(false);
    }
  }

  const CHART_ICON_LIB   = { bar:'📊', pie:'🥧', line:'📈', area:'📉', number:'🔢', table:'📋' };
  const METRIC_LABEL_LIB = { count:'Count', passRate:'Pass Rate', failRate:'Fail Rate', avgResults:'Avg Results' };

  const set       = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setFilter = (key, val) => setForm(f => ({ ...f, filters: { ...f.filters, [key]: val } }));
  const setLayout = (key, val) => setForm(f => ({ ...f, layout:  { ...f.layout,  [key]: val } }));

  const groupByOpts = GROUP_BY_OPTIONS[form.dataSource] || [];

  async function runPreview() {
    try {
      setPrevLoading(true);
      setPrevError('');
      setPreview(null);
      const res = await api.post('/dashboards/preview-data', { ...form, filters: { ...form.filters, projectId } });
      const payload = res.data?.data ?? res.data ?? [];
      setPreview(normalizeChartData(payload));
    } catch (err) {
      setPrevError(err.response?.data?.error || 'Preview failed');
    } finally {
      setPrevLoading(false);
    }
  }

  // Section heading helper
  const SectionHead = ({ n, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: 'var(--zq-primary)', color: '#fff',
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>{n}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--zq-text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</span>
    </div>
  );

  return (
    <div className="db-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="db-modal">

        {/* Header */}
        <div className="db-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'var(--zq-primary-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              {tab === 'library' ? '🗂️' : (CHART_TYPES.find(t => t.value === form.type)?.icon || '📊')}
            </span>
            <div>
              <h3 style={{ margin: 0 }}>{isEdit ? 'Edit Widget' : 'Add Widget'}</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--zq-text-muted)' }}>
                {isEdit ? `Update this widget's configuration` : 'Pick from the library or build a custom widget'}
              </p>
            </div>
          </div>
          <button className="db-icon-btn" onClick={onClose} style={{ fontSize: 16, padding: '6px 10px' }}>✕</button>
        </div>

        {/* Tab bar — hide when editing an existing widget */}
        {!isEdit && (
          <div style={{ display:'flex', borderBottom:'1px solid var(--zq-border)', padding:'0 24px', gap:0 }}>
            {[['library','🗂️ From Library'],['custom','✏️ Custom Widget']].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} style={{
                padding:'9px 18px', fontSize:13, fontWeight:600, border:'none', background:'none', cursor:'pointer',
                borderBottom: tab === key ? '2px solid var(--zq-primary)' : '2px solid transparent',
                color: tab === key ? 'var(--zq-primary)' : 'var(--zq-text-muted)',
                transition:'all .15s',
              }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="db-modal-body" style={{ maxHeight: tab === 'library' ? '62vh' : undefined, overflowY: tab === 'library' ? 'auto' : undefined }}>

          {/* ── Library tab ──────────────────────────────────────────────── */}
          {tab === 'library' && (
            <>
              {libLoadErr && <div className="db-widget-error">{libLoadErr}</div>}

              {/* Category sub-tabs */}
              <div style={{ display:'flex', gap:4, flexWrap:'nowrap', overflowX:'auto', marginBottom:16,
                            borderBottom:'1px solid var(--zq-border)', paddingBottom:0 }}>
                {libCategories.map(cat => (
                  <button key={cat} onClick={() => setLibTab(cat)} style={{
                    padding:'6px 14px', fontSize:12, fontWeight:600, border:'none', background:'none', cursor:'pointer',
                    borderBottom: cat === libTab ? '2px solid var(--zq-primary)' : '2px solid transparent',
                    color: cat === libTab ? 'var(--zq-primary)' : 'var(--zq-text-muted)',
                    whiteSpace:'nowrap', transition:'all .15s',
                  }}>{cat}</button>
                ))}
              </div>

              {/* Bulk controls */}
              {libVisible.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={{ fontSize:12, color:'var(--zq-text-muted)' }}>
                    {selected.size > 0
                      ? <><strong style={{ color:'var(--zq-primary)' }}>{selected.size}</strong> selected</>
                      : `${libVisible.length} widget${libVisible.length !== 1 ? 's' : ''} available`}
                  </span>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={selectAllLib} className="db-btn db-btn-secondary" style={{ padding:'3px 10px', fontSize:11 }}>Select All</button>
                    {selected.size > 0 && <button onClick={clearLib} className="db-btn db-btn-secondary" style={{ padding:'3px 10px', fontSize:11 }}>Clear</button>}
                  </div>
                </div>
              )}

              {/* Widget grid */}
              <div className="db-lib-grid">
                {libVisible.map(t => {
                  const isSel = selected.has(t.id);
                  return (
                    <button key={t.id} className={`db-lib-card ${isSel ? 'selected' : ''}`} onClick={() => toggleLib(t.id)}>
                      <div className="db-lib-check">
                        {isSel && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1.5,5 4,7.5 8.5,2.5"/>
                          </svg>
                        )}
                      </div>
                      <div className="db-lib-icon">{CHART_ICON_LIB[t.type] || '📊'}</div>
                      <div className="db-lib-info">
                        <div className="db-lib-title">{t.title}</div>
                        <div className="db-lib-desc">{t.description}</div>
                        <div className="db-lib-meta">
                          <span className="db-lib-badge cat">{t.categoryIcon} {t.category}</span>
                          <span className="db-lib-badge">{t.type}</span>
                          <span className="db-lib-badge">{METRIC_LABEL_LIB[t.metric] || t.metric}</span>
                          {t.filters?.dateRange && <span className="db-lib-badge date">{t.filters.dateRange}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Custom tab ───────────────────────────────────────────────────── */}
          {(tab === 'custom' || isEdit) && (<>

          {/* 1 — Title */}
          <div>
            <SectionHead n="1" label="Widget Title" />
            <input
              className="db-input"
              placeholder="e.g. Test Runs by Status, Pass Rate This Week…"
              value={form.title}
              onChange={e => set('title', e.target.value)}
            />
          </div>

          {/* 2 — Chart type */}
          <div>
            <SectionHead n="2" label="Visualisation Type" />
            <div className="db-chart-type-grid">
              {CHART_TYPES.map(ct => (
                <button
                  key={ct.value}
                  className={`db-chart-type-card ${form.type === ct.value ? 'active' : ''}`}
                  onClick={() => set('type', ct.value)}
                >
                  <span className="db-chart-type-icon">{ct.icon}</span>
                  <span className="db-chart-type-label">{ct.label}</span>
                  <span className="db-chart-type-desc">{ct.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 3 — Data source */}
          <div>
            <SectionHead n="3" label="Data Source" />
            <div className="db-source-grid">
              {DATA_SOURCES.map(ds => (
                <button
                  key={ds.value}
                  className={`db-source-card ${form.dataSource === ds.value ? 'active' : ''}`}
                  onClick={() => {
                    set('dataSource', ds.value);
                    set('groupBy', GROUP_BY_OPTIONS[ds.value]?.[0]?.value || 'status');
                  }}
                >
                  <span style={{ fontSize: 20 }}>{ds.icon}</span>
                  {ds.label}
                </button>
              ))}
            </div>
          </div>

          {/* 4 — Group by + Metric */}
          <div>
            <SectionHead n="4" label="Aggregation" />
            <div className="db-form-row">
              <div className="db-form-group">
                <label className="db-form-label">Group By</label>
                <select className="db-select" value={form.groupBy} onChange={e => set('groupBy', e.target.value)}>
                  {groupByOpts.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <div className="db-form-group">
                <label className="db-form-label">Metric</label>
                <select className="db-select" value={form.metric} onChange={e => set('metric', e.target.value)}>
                  {METRIC_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* 5 — Filters */}
          <div>
            <SectionHead n="5" label="Filters" />
            <div className="db-filters-row">
              <div className="db-form-group">
                <label className="db-form-label">Date Range</label>
                <select className="db-select" value={form.filters.dateRange} onChange={e => setFilter('dateRange', e.target.value)}>
                  {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              {(form.dataSource === 'test_runs' || form.dataSource === 'test_results') && (
                <div className="db-form-group">
                  <label className="db-form-label">Run Type</label>
                  <select className="db-select" value={form.filters.runType} onChange={e => setFilter('runType', e.target.value)}>
                    <option value="">All</option>
                    <option value="manual">Manual</option>
                    <option value="automated">Automated</option>
                  </select>
                </div>
              )}
              <div className="db-form-group">
                <label className="db-form-label">Status</label>
                <input className="db-input" placeholder="All statuses" value={form.filters.status}
                  onChange={e => setFilter('status', e.target.value)} />
              </div>
            </div>
          </div>

          {/* 6 — Width */}
          <div>
            <SectionHead n="6" label="Widget Width" />
            <div className="db-size-row" style={{ background: 'none', border: 'none', padding: 0, gap: 8 }}>
              {SIZES.map(s => (
                <button key={s.w}
                  className={`db-size-pill ${form.layout.w === s.w ? 'active' : ''}`}
                  style={{ padding: '5px 16px', fontSize: 12 }}
                  onClick={() => setLayout('w', s.w)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced query */}
          <div style={{ borderTop: '1px solid var(--zq-border)', paddingTop: 16 }}>
            <button className="db-toggle-advanced" onClick={() => setAdvanced(a => !a)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                {advanced
                  ? <polyline points="2,4 6,8 10,4"/>
                  : <polyline points="4,2 8,6 4,10"/>}
              </svg>
              Advanced: Custom MongoDB Pipeline
              {advanced && (
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', background: 'var(--zq-warning-light)', color: 'var(--zq-warning)', borderRadius: 10 }}>ACTIVE</span>
              )}
            </button>
            {advanced && (
              <div className="db-advanced-wrap">
                <p className="db-advanced-hint">
                  Enter a raw MongoDB aggregation pipeline as a JSON array. This bypasses all filter controls above.
                  Each stage must produce <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>_id</code> and <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>value</code> fields.
                </p>
                <textarea
                  className="db-query-editor"
                  rows={7}
                  placeholder={'[\n  { "$match": { "status": "finished" } },\n  { "$group": { "_id": "$runType", "value": { "$sum": 1 } } },\n  { "$sort": { "value": -1 } }\n]'}
                  value={form.customQuery}
                  onChange={e => set('customQuery', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="db-preview-pane">
            <div className="db-preview-header">
              <span>Live Preview</span>
              <button className="db-btn db-btn-secondary" onClick={runPreview} disabled={prevLoading}
                style={{ padding: '4px 12px', fontSize: 12 }}>
                {prevLoading
                  ? <><span style={{ display: 'inline-block', width: 11, height: 11, border: '2px solid #c7d2fe', borderTopColor: 'var(--zq-primary)', borderRadius: '50%', animation: 'db-spin .7s linear infinite', marginRight: 6 }} />Running…</>
                  : '▶ Run Preview'}
              </button>
            </div>
            <div className="db-preview-body" style={{ minHeight: 200 }}>
              {prevError && (
                <div className="db-widget-error" style={{ maxWidth: 400 }}>⚠ {prevError}</div>
              )}
              {!prevError && !preview && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 36, marginBottom: 8, opacity: .3 }}>📊</div>
                  <p className="db-preview-placeholder">Click "Run Preview" to visualise your configuration</p>
                </div>
              )}
              {!prevError && preview && (
                <div style={{ width: '100%', height: preview.single !== undefined ? 'auto' : 200 }}>
                  <ChartRenderer type={form.type} data={preview} />
                </div>
              )}
            </div>
          </div>{/* end db-preview-pane */}
          </>)}
        </div>{/* end db-modal-body */}

        {/* Footer */}
        <div className="db-modal-footer">
          <button className="db-btn db-btn-secondary" onClick={onClose}>Cancel</button>
          {tab === 'library' && !isEdit ? (
            <button
              className="db-btn db-btn-primary"
              disabled={selected.size === 0 || libSaving}
              onClick={applyLibrary}
            >
              {libSaving
                ? <><span style={{ display:'inline-block', width:12, height:12, border:'2px solid #c7d2fe', borderTopColor:'#fff', borderRadius:'50%', animation:'db-spin .7s linear infinite', marginRight:6 }} />Adding…</>
                : `＋ Add ${selected.size > 0 ? selected.size + ' ' : ''}Widget${selected.size !== 1 ? 's' : ''}`}
            </button>
          ) : (
            <button className="db-btn db-btn-primary" onClick={() => onSave(form)}>
              {isEdit ? 'Save Changes' : '＋ Add Widget'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Create Dashboard Modal ───────────────────────────────────────────────────

function CreateDashboardModal({ onSave, onClose }) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared]       = useState(false);
  const [isDefault, setIsDefault]     = useState(false);

  return (
    <div className="db-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="db-modal" style={{ maxWidth: 480 }}>

        <div className="db-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'var(--zq-primary-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>📊</span>
            <div>
              <h3 style={{ margin: 0 }}>New Dashboard</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--zq-text-muted)' }}>Create a personalised analytics view</p>
            </div>
          </div>
          <button className="db-icon-btn" onClick={onClose} style={{ fontSize: 16, padding: '6px 10px' }}>✕</button>
        </div>

        <div className="db-modal-body">
          <div className="db-form-group">
            <label className="db-form-label">Dashboard Name *</label>
            <input
              className="db-input"
              placeholder="e.g. Sprint Overview, Release 3.0 Health…"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="db-form-group">
            <label className="db-form-label">Description</label>
            <textarea
              className="db-input"
              rows={2}
              placeholder="Optional: what does this dashboard show?"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          <div style={{
            display: 'flex', gap: 24, padding: '12px 16px',
            background: 'var(--zq-border-light)',
            borderRadius: 'var(--zq-radius-sm)',
            border: '1px solid var(--zq-border)',
          }}>
            <label className="db-checkbox-label">
              <input type="checkbox" checked={isShared} onChange={e => setIsShared(e.target.checked)} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Share with team</div>
                <div style={{ fontSize: 11, color: 'var(--zq-text-muted)' }}>Visible to all project members</div>
              </div>
            </label>
            <label className="db-checkbox-label">
              <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Set as default</div>
                <div style={{ fontSize: 11, color: 'var(--zq-text-muted)' }}>Open first when entering project</div>
              </div>
            </label>
          </div>
        </div>

        <div className="db-modal-footer">
          <button className="db-btn db-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="db-btn db-btn-primary"
            disabled={!name.trim()}
            onClick={() => onSave({ name: name.trim(), description, isShared, isDefault })}
          >
            Create Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard Page ───────────────────────────────────────────────────────

export default function Dashboard() {
  const { projectKey } = useParams();

  const [dashboards, setDashboards]   = useState([]);
  const [activeIdx, setActiveIdx]     = useState(0);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [projectId, setProjectId]     = useState(null);

  const [showCreateDash, setShowCreateDash]   = useState(false);
  const [showWidgetModal, setShowWidgetModal]     = useState(false);
  const [showWidgetTab, setShowWidgetTab]         = useState('library'); // 'library' | 'custom'
  const [editingWidget, setEditingWidget]         = useState(null);

  // Resolve projectId from projectKey
  useEffect(() => {
    async function loadProject() {
      try {
        const res = await api.get('/projects');
        const projects = res.data?.data || res.data || [];
        const proj = projects.find(p =>
          (p.key || p.projectKey || '').toLowerCase() === projectKey.toLowerCase()
        );
        if (proj) setProjectId(proj._id || proj.id);
      } catch (err) {
        console.error('Failed to load project:', err);
      }
    }
    if (projectKey) loadProject();
  }, [projectKey]);

  // Load dashboards for this project
  const loadDashboards = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/dashboards?projectId=${projectId}`);
      const list = res.data?.data || res.data || [];
      setDashboards(list);
      if (list.length > 0 && activeIdx >= list.length) setActiveIdx(0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  }, [projectId, activeIdx]);

  useEffect(() => { loadDashboards(); }, [loadDashboards]);

  const activeDashboard = dashboards[activeIdx] || null;

  // ── Create dashboard ──
  async function handleCreateDashboard(form) {
    try {
      await api.post('/dashboards', { ...form, projectId });
      setShowCreateDash(false);
      await loadDashboards();
      setActiveIdx(dashboards.length); // switch to new tab
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create dashboard');
    }
  }

  // ── Delete dashboard ──
  async function handleDeleteDashboard() {
    if (!activeDashboard) return;
    if (!window.confirm(`Delete dashboard "${activeDashboard.name}"?`)) return;
    try {
      await api.delete(`/dashboards/${activeDashboard._id}`);
      setActiveIdx(Math.max(0, activeIdx - 1));
      await loadDashboards();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete dashboard');
    }
  }

  // ── Toggle share ──
  async function handleToggleShare() {
    if (!activeDashboard) return;
    try {
      await api.put(`/dashboards/${activeDashboard._id}`, { isShared: !activeDashboard.isShared });
      await loadDashboards();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update dashboard');
    }
  }

  // ── Add / Edit widget ──
  async function handleSaveWidget(form) {
    if (!activeDashboard) return;
    try {
      const payload = { ...form, filters: { ...form.filters, projectId } };
      if (editingWidget?._id) {
        await api.put(`/dashboards/${activeDashboard._id}/widgets/${editingWidget._id}`, payload);
      } else {
        await api.post(`/dashboards/${activeDashboard._id}/widgets`, payload);
      }
      setShowWidgetModal(false);
      setEditingWidget(null);
      await loadDashboards();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save widget');
    }
  }

  // ── Delete widget ──
  async function handleDeleteWidget(widgetId) {
    if (!activeDashboard) return;
    if (!window.confirm('Remove this widget?')) return;
    try {
      await api.delete(`/dashboards/${activeDashboard._id}/widgets/${widgetId}`);
      await loadDashboards();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove widget');
    }
  }

  // ── Resize widget ──
  async function handleResize(widgetId, newW) {
    if (!activeDashboard) return;
    try {
      const updated = activeDashboard.widgets.map(w =>
        (w._id === widgetId || w.id === widgetId)
          ? { ...w, layout: { ...w.layout, w: newW } }
          : w
      );
      await api.put(`/dashboards/${activeDashboard._id}`, { widgets: updated });
      await loadDashboards();
    } catch (err) {
      console.error('Resize failed:', err);
    }
  }

  // ── Open edit widget modal ──
  function openEditWidget(widget) {
    setEditingWidget(widget);
    setShowWidgetTab('custom');
    setShowWidgetModal(true);
  }

  // ── Render ──
  return (
    <div className="db-page">

      {/* Page header — matches ZipQA .page-header pattern */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {activeDashboard
              ? activeDashboard.description || 'Customisable analytics for this project'
              : 'Customisable analytics for this project'}
          </p>
        </div>
        <div className="page-actions">
          {activeDashboard && (
            <>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleToggleShare}
                title={activeDashboard.isShared ? 'Click to make private' : 'Click to share with team'}
              >
                {activeDashboard.isShared
                  ? <><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}><rect x="3" y="7" width="10" height="8" rx="1"/><path d="M5 7V5a3 3 0 016 0v2" strokeLinecap="round"/></svg>Shared</>
                  : <><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}><rect x="3" y="7" width="10" height="8" rx="1"/><path d="M5 7V5a3 3 0 016 0v2" strokeLinecap="round"/></svg>Private</>
                }
              </button>
              <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteDashboard}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ marginRight: 5 }}>
                  <polyline points="2,4 14,4"/><path d="M6 4V2h4v2"/><path d="M3 4l1 10h8l1-10" strokeLinecap="round"/>
                </svg>
                Delete
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingWidget(null); setShowWidgetTab('library'); setShowWidgetModal(true); }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 5 }}>
                  <line x1="8" y1="2" x2="8" y2="14" strokeLinecap="round"/>
                  <line x1="2" y1="8" x2="14" y2="8" strokeLinecap="round"/>
                </svg>
                Add Widget
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Dashboard tab bar */}
      <div className="db-tab-bar">
        {dashboards.map((d, i) => (
          <button
            key={d._id || i}
            className={`db-tab ${i === activeIdx ? 'active' : ''}`}
            onClick={() => setActiveIdx(i)}
          >
            {d.name}
            {d.isShared && <span className="db-tab-badge">shared</span>}
          </button>
        ))}
        <button className="db-tab db-tab-new" onClick={() => setShowCreateDash(true)}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2">
            <line x1="6" y1="1" x2="6" y2="11" strokeLinecap="round"/>
            <line x1="1" y1="6" x2="11" y2="6" strokeLinecap="round"/>
          </svg>
          New Dashboard
        </button>
      </div>

      {/* Content area */}
      {loading ? (
        <div className="zq-spinner-wrap">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : !activeDashboard ? (
        <div className="db-empty-state">
          <div className="db-empty-icon">📊</div>
          <h3>No dashboards yet</h3>
          <p>Create your first dashboard to start visualising your test results and project health.</p>
          <button className="btn btn-primary" onClick={() => setShowCreateDash(true)}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: 6 }}>
              <line x1="6" y1="1" x2="6" y2="11" strokeLinecap="round"/>
              <line x1="1" y1="6" x2="11" y2="6" strokeLinecap="round"/>
            </svg>
            Create Dashboard
          </button>
        </div>
      ) : activeDashboard.widgets?.length === 0 ? (
        <div className="db-empty-state">
          <div className="db-empty-icon">🧩</div>
          <h3>This dashboard has no widgets</h3>
          <p>Get started quickly with pre-built analytics widgets, or build your own custom widget.</p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', marginTop:4 }}>
            <button className="btn btn-primary" onClick={() => { setEditingWidget(null); setShowWidgetTab('library'); setShowWidgetModal(true); }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ marginRight: 6 }}>
                <rect x="2" y="2" width="5" height="5" rx="1"/><rect x="9" y="2" width="5" height="5" rx="1"/>
                <rect x="2" y="9" width="5" height="5" rx="1"/><rect x="9" y="9" width="5" height="5" rx="1"/>
              </svg>
              Browse Widget Library
            </button>
            <button className="btn btn-outline-secondary" onClick={() => { setEditingWidget(null); setShowWidgetTab('custom'); setShowWidgetModal(true); }}>
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: 6 }}>
                <line x1="6" y1="1" x2="6" y2="11" strokeLinecap="round"/>
                <line x1="1" y1="6" x2="11" y2="6" strokeLinecap="round"/>
              </svg>
              Custom Widget
            </button>
          </div>
          {/* Quick-pick category strips */}
          <div style={{ marginTop:32, width:'100%', maxWidth:700 }}>
            <p style={{ fontSize:13, color:'var(--zq-text-muted)', marginBottom:16, textAlign:'center' }}>Or jump straight to a category:</p>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              {[['🟢','Status'],['📅','Activity'],['📈','Progress'],['🐛','Quality'],['👥','Workload'],['✅','Test Cases'],['📋','Requirements']].map(([icon, cat]) => (
                <button
                  key={cat}
                  onClick={() => { setEditingWidget(null); setShowWidgetTab('library'); setShowWidgetModal(true); }}
                  style={{
                    display:'flex', alignItems:'center', gap:6,
                    padding:'7px 14px', borderRadius:20,
                    border:'1px solid var(--zq-border)', background:'var(--zq-surface)',
                    fontSize:12, fontWeight:600, color:'var(--zq-text-secondary)',
                    cursor:'pointer', transition:'all .15s',
                  }}
                >
                  <span>{icon}</span>{cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="db-widget-grid">
          {activeDashboard.widgets.map(w => (
            <WidgetCard
              key={w._id}
              widget={w}
              dashboardId={activeDashboard._id}
              onEdit={openEditWidget}
              onDelete={handleDeleteWidget}
              onResize={handleResize}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateDash && (
        <CreateDashboardModal
          onSave={handleCreateDashboard}
          onClose={() => setShowCreateDash(false)}
        />
      )}
      {showWidgetModal && (
        <WidgetModal
          initial={editingWidget}
          projectId={projectId}
          dashboardId={activeDashboard?._id}
          initialTab={showWidgetTab}
          onSave={handleSaveWidget}
          onApplied={() => { setShowWidgetModal(false); loadDashboards(); }}
          onClose={() => { setShowWidgetModal(false); setEditingWidget(null); }}
        />
      )}
    </div>
  );
}
